import {
  ApiException,
  CustomerIndexViewModel,
  CustomersApi,
  ManagerModel,
  TenantViewModel,
  UnitAccountIndexViewModel,
  UserSettingsInputModel,
  UserSettingsViewModel,
} from '@ail/customers-api'
import { createApi } from '@reduxjs/toolkit/query/react'
import { USER_MANAGER } from '../../auth/UserManager'
import { Environment } from '../../environment'
import { debounce } from '../../helpers/Misc'
import { getBaseQuery } from './apiQuery'

const client = new CustomersApi({
  baseUrl: Environment.CustomersApiUrl,
  getToken: async () => {
    const user = await USER_MANAGER.getUser()
    return user?.access_token ?? ''
  },
})

const putUserSettingsDebounced = debounce((model: UserSettingsInputModel) => {
  client.putUserSettings(model)
}, 1000)

export const customersApi = createApi({
  reducerPath: 'customersApi',
  baseQuery: getBaseQuery<ApiException>(),
  endpoints: (builder) => ({
    getAllCustomers: builder.query<CustomerIndexViewModel[], void>({
      query: () => client.allCustomers(),
    }),
    getAllUnits: builder.query<UnitAccountIndexViewModel[], void>({
      query: () => client.allUnits(),
    }),
    getCustomerManagers: builder.query<ManagerModel[], string>({
      query: (customerId: string) => client.getCustomerManagers(customerId),
    }),
    getTenant: builder.query<TenantViewModel, string>({
      query: (tenantId: string) => client.getTenant(tenantId),
    }),
    getTenants: builder.query<TenantViewModel[], void>({
      query: () => client.getTenants(),
    }),
    getSettings: builder.query<UserSettingsViewModel, { remote?: boolean }>({
      query: async ({ remote }) => {
        if (remote) {
          const response = await client.getUserSettings()
          return response
        } else {
          const data = Object.keys(localStorage).reduce((r, setting) => {
            const entry = localStorage.getItem(setting)
            try {
              const obj = JSON.parse(entry!)
              r[setting] = obj
            } catch {
              r[setting] = entry
            }
            return r
          }, {} as UserSettingsViewModel)
          return { data }
        }
      },
    }),
    updateSettings: builder.mutation<void, { remote?: boolean; model: UserSettingsInputModel }>({
      query: async ({ remote, model }) => {
        if (remote) {
          putUserSettingsDebounced(model)
        } else {
          Object.keys(model).forEach((setting) => {
            const obj = model[setting]
            let str = obj
            if (typeof obj !== 'string') {
              str = JSON.stringify(model[setting])
            }
            localStorage.setItem(setting, str)
          })
        }
        return { data: undefined }
      },
      onQueryStarted: ({ remote, model }, { dispatch }) => {
        dispatch(customersApi.util.upsertQueryData('getSettings', { remote }, model))
      },
    }),
  }),
})

export const {
  useGetAllUnitsQuery,
  useGetAllCustomersQuery,
  useGetTenantQuery,
  useGetTenantsQuery,
  useLazyGetTenantsQuery,
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useGetCustomerManagersQuery,
} = customersApi
